import { api, apiGet } from '../../functions/Apiwithouttoken';
import { formatNumber } from '../../utils/formatter';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback, memo } from 'react';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { trendStyle, EndDot } from './chartSeriesStyle';
import { ChevronDown } from 'lucide-react';

const timeIntervals = [
  { value: '1W', label: '1W' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
  { value: 'ALL', label: 'ALL' }
];

// Prepended when `enableHourly` is set — currently only PSA-10 has hourly data.
const HOURLY_INTERVAL = { value: '1H', label: '24H' };

const INTERVAL_DAYS = {
  '1H': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365, 'ALL': Infinity
};

const INTERVAL_TO_RANGE = {
  '1W': '7D', '1M': '30D', '3M': '90D', '6M': '180D', '1Y': '1Y', 'ALL': 'ALL'
};

const MobileChartSkeleton = () => (
  <div className="md:hidden">
    <div className="h-80 animate-pulse">
      <div className="h-full bg-gray-200 rounded-lg mx-4 mt-4"></div>
    </div>
  </div>
);

const DesktopChartSkeleton = () => (
  <div className="hidden md:block">
    <div className="w-full bg-white p-6 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-6 animate-pulse">
        <div>
          <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-64"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="h-96 bg-gray-200 rounded-lg animate-pulse"></div>
    </div>
  </div>
);

const CustomActiveDot = ({ cx, cy, color = "var(--cat-accent, #7A2BE4)" }) => (
  <g>
    <defs>
      <filter id="pocActiveDotShadow" x="-100%" y="-100%" width="300%" height="300%">
        <feDropShadow dx="0" dy="4" stdDeviation="10" floodColor="#000000" floodOpacity="0.2" />
      </filter>
    </defs>
    <circle cx={cx} cy={cy} r={9} fill="white" stroke={color} strokeWidth={0.6} filter="url(#pocActiveDotShadow)" />
    <circle cx={cx} cy={cy} r={5} fill={color} stroke="var(--surface, #FFFFFF)" strokeWidth={3} />
  </g>
);

/**
 * Generic server-backed index chart.
 *
 * Defaults to the /poc/chart-data endpoint, which serves proof-of-concept
 * markets (VIX, Coffee, Leveraged Perps, etc.) keyed by `marketKey`.
 *
 * Live index markets that graduated out of PoC pass their own `endpoint` —
 * e.g. PSA-10 uses 'psa10/chart-data', which takes only a range. Any endpoint
 * returning { success, data: { PriceData: [{date, price}], currentValue,
 * change24h } } works here.
 *
 * When `enableHourly` is set (PSA-10 only, today), a "24H" interval is added
 * that instead fetches from PSA-10's own /psa10/hourly-history endpoint —
 * the only market with hourly data — regardless of `endpoint`/`marketKey`.
 */
const PocTradingView = forwardRef(({
  marketKey,
  displayName = '',
  initialInterval = null,
  defaultInterval = '1M',
  endpoint = 'poc/chart-data',
  enableHourly = false
}, ref) => {
  const [data, setData] = useState([]);
  const [interval, setInterval] = useState(initialInterval || defaultInterval);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentValue, setCurrentValue] = useState(null);
  const [dailyChange, setDailyChange] = useState(null);

  const isHourlyView = enableHourly && interval === '1H';
  const intervalOptions = enableHourly ? [HOURLY_INTERVAL, ...timeIntervals] : timeIntervals;

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const days = INTERVAL_DAYS[interval] || 30;
    if (days === Infinity) return data;
    const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
    const filtered = data.filter(item => new Date(item.date).getTime() >= cutoff);
    return filtered.length > 0 ? filtered : data.slice(-Math.min(7, data.length));
  }, [data, interval]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // "24H" — PSA-10 only — reads the dedicated hourly endpoint (its own
      // psa10candles collection, granularity '1h'), not `endpoint` at all.
      const range = INTERVAL_TO_RANGE[interval] || '30D';
      let response;
      if (interval === '1H' && enableHourly) {
        response = await apiGet('psa10/hourly-history?hours=24');
      } else {
        // marketKey selects the market on the generic /poc endpoint; dedicated
        // per-index endpoints ignore it and take the range alone.
        const body = marketKey ? { marketKey, range } : { range };
        response = await api(endpoint, body);
      }

      // Daily/PoC responses use `PriceData`; the hourly endpoint uses `history`.
      const rawPoints = response?.data?.PriceData || response?.data?.history;

      if (response.success && rawPoints) {
        const priceData = rawPoints.map(item => ({
          date: item.date,
          price: typeof item.price === 'number' ? item.price : parseFloat(item.price)
        }));

        setData(priceData);

        const apiCurrentValue = response.data.currentValue;
        if (apiCurrentValue != null) {
          setCurrentValue(apiCurrentValue);
        } else if (priceData.length > 0) {
          setCurrentValue(priceData[priceData.length - 1].price);
        } else {
          setCurrentValue(null);
        }

        setDailyChange(response.data.change24h ?? null);
      } else {
        setData([]);
        setCurrentValue(null);
        setDailyChange(null);
      }
    } catch (error) {
      console.error(`Error fetching chart data from ${endpoint} for ${marketKey || displayName}:`, error);
      setData([]);
      setCurrentValue(null);
      setDailyChange(null);
    } finally {
      setLoading(false);
    }
  }, [interval, marketKey, endpoint, displayName, enableHourly]);

  useImperativeHandle(ref, () => ({
    handleIntervalChange: (newInterval) => { setInterval(newInterval); fetchData(); },
    getCurrentInterval: () => interval,
    refreshData: fetchData
  }), [interval, fetchData]);

  useEffect(() => {
    if (initialInterval && initialInterval !== interval) setInterval(initialInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInterval]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    if (isHourlyView) {
      const hh = String(date.getUTCHours()).padStart(2, '0');
      const mm = String(date.getUTCMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
  }, [isHourlyView]);

  const formatYAxisPrice = useCallback((price) => {
    return formatNumber(price, { currency: false, minDecimals: 0, maxDecimals: 0 });
  }, []);

  const CustomTooltip = memo(({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const date = new Date(item.payload.date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateStr = isHourlyView
      ? `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')} UTC`
      : `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    return (
      <div className="bg-white p-2 md:p-3 shadow-lg rounded-lg border text-xs md:text-sm max-w-[150px] md:max-w-none">
        <p className="text-gray-600 truncate">{dateStr}</p>
        <p className="font-semibold">${formatNumber(item.value, { currency: false, minDecimals: 2, maxDecimals: 2 })}</p>
      </div>
    );
  });

  // The skeleton is for the FIRST load only. `fetchData` sets `loading` on every
  // poll, so gating the chart on it alone tore the whole thing down and put the
  // placeholder back each refresh — the series blanked, remounted, and replayed
  // its reveal on a timer. Once there is data on screen, a refresh updates it in
  // place and the chart never disappears.
  if ((loading && data.length === 0) || !filteredData || filteredData.length === 0) {
    return (
      <div className="poc-chart-container">
        <MobileChartSkeleton />
        <DesktopChartSkeleton />
      </div>
    );
  }

  const isMobileControlled = initialInterval !== null;

  /* Direction colour for the visible range — see chartSeriesStyle. */
  const series = trendStyle(filteredData);
  const chartTitle = displayName || marketKey;

  return (
    <div className="poc-chart-container">
      {/* Mobile View */}
      <div className="md:hidden flex justify-center">
        <div className="h-80 w-full ce-chart-anim" key={`a-m-${interval}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={filteredData} margin={{ top: 10, right: 15, left: 5, bottom: 10 }}>
              {/* Horizontals only, and barely there — enough to read a level across, not enough to compete with the series. */}
              <CartesianGrid stroke="var(--line-2)" strokeOpacity={0.6} vertical={false} />
              <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fill: 'var(--ink-3)', fontSize: 11 }} axisLine={false} tickLine={false} type="category" />
              <YAxis tickFormatter={formatYAxisPrice} tick={{ fill: 'var(--ink-3)', fontSize: 11 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} width={50} />
              <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 10, pointerEvents: 'none', maxWidth: '90vw' }} />
              <defs>
                <linearGradient id="ceAreaFill-poc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={series.color} stopOpacity={0.22} />
                  <stop offset="55%" stopColor={series.color} stopOpacity={0.06} />
                  <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              {series.lastValue !== null && (
                <ReferenceLine y={series.lastValue} stroke={series.color} strokeOpacity={0.35} strokeWidth={1} />
              )}
              <Area type="monotone" dataKey="price" stroke={series.color} strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round" fill="url(#ceAreaFill-poc)"
                  style={{ filter: `drop-shadow(0 6px 14px ${series.glow})` }} dot={<EndDot lastIndex={filteredData.length - 1} color={series.color} />} activeDot={<CustomActiveDot color={series.color} />} isAnimationActive={true}
                  animationDuration={900}
                  animationEasing="ease-out" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="w-full bg-white p-6 rounded-xl shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{chartTitle} Chart</h2>
              {currentValue !== null && (
                <p className="text-sm text-gray-500 mt-1">
                  Current Value: {formatNumber(currentValue, { currency: false, minDecimals: 2, maxDecimals: 2 })}
                </p>
              )}
            </div>
            {!isMobileControlled && (
              <div className="relative">
                <button onClick={() => setIsOpen(!isOpen)} className="ce-menu-trigger" style={{ background: "var(--cat-accent-soft, #F8F4FC)" }}>
                  <span className="text-sm font-medium" style={{ color: "var(--cat-accent, #7C2BE8)" }}>
                    {intervalOptions.find((item) => item.value === interval)?.label || interval}
                  </span>
                  <ChevronDown className="w-4 h-4" style={{ color: "var(--cat-accent, #7C2BE8)" }} />
                </button>
                {isOpen && (
                  <div className="ce-menu" role="menu">
                    {intervalOptions.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={interval === item.value}
                        onClick={() => {
                          setInterval(item.value);
                          setIsOpen(false);
                        }}
                        className="ce-menu-item"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="h-96 ce-chart-anim" key={`a-${interval}`}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={filteredData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                {/* Horizontals only, and barely there — enough to read a level across, not enough to compete with the series. */}
                <CartesianGrid stroke="var(--line-2)" strokeOpacity={0.6} vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fill: 'var(--ink-3)', fontSize: 11 }} axisLine={false} tickLine={false} type="category" />
                <YAxis tickFormatter={formatYAxisPrice} tick={{ fill: 'var(--ink-3)', fontSize: 11 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  <linearGradient id="ceAreaFill-poc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.22} />
                    <stop offset="55%" stopColor={series.color} stopOpacity={0.06} />
                    <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                {series.lastValue !== null && (
                <ReferenceLine y={series.lastValue} stroke={series.color} strokeOpacity={0.35} strokeWidth={1} />
              )}
              <Area type="monotone" dataKey="price" stroke={series.color} strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round" fill="url(#ceAreaFill-poc)"
                  style={{ filter: `drop-shadow(0 6px 14px ${series.glow})` }} dot={<EndDot lastIndex={filteredData.length - 1} color={series.color} />} activeDot={<CustomActiveDot color={series.color} />} isAnimationActive={true}
                  animationDuration={900}
                  animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
});

PocTradingView.displayName = 'PocTradingView';

export default PocTradingView;
